import { Button, Checkbox, Dialog } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export type ConversationStatusDialogVariant = "close" | "reopen";

export interface ConversationStatusDialogProps {
  variant?: ConversationStatusDialogVariant;
  conversationName: string;
  isOpen: boolean;
  onRequestClose: () => void;
  onConfirm: (options: {
    skipFutureAlerts: boolean;
    variant: ConversationStatusDialogVariant;
  }) => void;
}

export const ConversationStatusDialog: React.FC<
  ConversationStatusDialogProps
> = ({ variant, conversationName, isOpen, onRequestClose, onConfirm }) => {
  const { t } = useTranslations();
  const [skipFutureAlerts, setSkipFutureAlerts] = useState(false);
  const [displayVariant, setDisplayVariant] = useState<
    ConversationStatusDialogVariant | undefined
  >();

  useEffect(() => {
    if (isOpen && variant) {
      setDisplayVariant(variant);
      setSkipFutureAlerts(false);
    }
  }, [isOpen, variant]);

  const title = useMemo(
    () =>
      displayVariant === "close"
        ? t({
            id: "client-support-page.close-conversation-emoji",
            defaultMessage: "💬 Close Conversation",
          })
        : t({
            id: "client-support-page.reopen-conversation-emoji",
            defaultMessage: "🔁 Reopen Conversation",
          }),
    [displayVariant, t]
  );

  const content = useMemo(
    () =>
      displayVariant === "close"
        ? t(
            {
              id: "client-support-page.close-conversation-confirmation",
              defaultMessage:
                "Are you sure you want to close conversation with {name}? You can reopen it anytime, or it will reopen automatically when the customer replies",
            },
            {
              name: (
                <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                  {conversationName}
                </span>
              ),
            }
          )
        : t(
            {
              id: "client-support-page.reopen-conversation-confirmation",
              defaultMessage:
                "Are you sure you want to reopen conversation with {name}?",
            },
            {
              name: (
                <span className="font-medium text-primary">
                  {conversationName}
                </span>
              ),
            }
          ),
    [conversationName, displayVariant, t]
  );

  const confirmLabel =
    displayVariant === "close"
      ? t({
          id: "common.close",
          defaultMessage: "Close",
        })
      : t({
          id: "client-support-page.reopen",
          defaultMessage: "Reopen",
        });

  const handleConfirm = () => {
    if (!displayVariant) {
      return;
    }

    onConfirm({ skipFutureAlerts, variant: displayVariant });
    onRequestClose();
  };

  return (
    <Dialog
      isOpen={isOpen && !!displayVariant}
      onClose={onRequestClose}
      className="flex w-full max-w-[480px] flex-col overflow-hidden rounded-2xl p-10"
    >
      <span className="font-secondary text-[22px] font-bold text-dark">
        {title}
      </span>
      <span className="mt-2.5 font-secondary text-sm leading-[21px] text-dark-700">
        {content}
      </span>
      <div className="mt-[18px]">
        <Checkbox
          label={t({
            id: "common.dont-ask-me-again",
            defaultMessage: "Don't ask me again",
          })}
          labelClassName="font-secondary text-[13px] text-dark-900 leading-[150%]"
          isChecked={skipFutureAlerts}
          onChange={(event) => setSkipFutureAlerts(event.target.checked)}
        />
      </div>
      <div className="mt-14 flex items-center gap-4">
        <Button
          onClick={onRequestClose}
          variant="tertiary"
          className="w-[140px]"
        >
          {t({
            id: "common.cancel",
            defaultMessage: "Cancel",
          })}
        </Button>
        <Button
          onClick={handleConfirm}
          iconEnd={<ArrowRightIcon />}
          className="flex-1"
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
};
